import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Logo } from "../components/AppShell";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger,
  DialogFooter,
} from "../components/ui/dialog";
import {
  Tabs, TabsList, TabsTrigger, TabsContent,
} from "../components/ui/tabs";
import {
  LogOut, RefreshCw, Trash2, Eye, Users, Layers, CircleDot, CheckCircle2,
  Link2, Bell, BookOpen, Briefcase, FileText, Plus, ExternalLink,
  CreditCard, Settings,
} from "lucide-react";
import {
  adminListLeads, adminUpdateStatus, adminUpdateLead, adminDeleteLead,
  adminStats, formatErr, adminMe, adminListVendors,
  adminListResources, adminCreateResource, adminDeleteResource,
  adminNotifications, adminMarkNotificationsRead, adminChangePassword,
  admin2faStatus, admin2faSetup, admin2faEnable, admin2faDisable,
  adminSendSecondPayment,
} from "../lib/api";
import { toast } from "sonner";

const STATUS_OPTS = [
  { value: "pendiente", label: "Pendiente", color: "#F59E0B" },
  { value: "en_proceso", label: "En proceso", color: "#3B82F6" },
  { value: "entregado", label: "Entregado", color: "#10B981" },
];

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [tab, setTab] = useState("leads");
  const [leads, setLeads] = useState([]);
  const [stats, setStats] = useState(null);
  const [me, setMe] = useState(null);
  const [vendors, setVendors] = useState([]);
  const [resources, setResources] = useState([]);
  const [notifs, setNotifs] = useState({ items: [], unread: 0 });
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");

  const load = async () => {
    setLoading(true);
    try {
      const [list, s, m, v, r, n] = await Promise.all([
        adminListLeads(), adminStats(), adminMe(), adminListVendors(),
        adminListResources(), adminNotifications(),
      ]);
      setLeads(list); setStats(s); setMe(m); setVendors(v); setResources(r); setNotifs(n);
    } catch (err) {
      if (err?.response?.status === 401) {
        localStorage.removeItem("el_admin_token"); navigate("/admin/login");
      } else { toast.error(formatErr(err)); }
    } finally { setLoading(false); }
  };

  useEffect(() => {
    if (!localStorage.getItem("el_admin_token")) { navigate("/admin/login"); return; }
    load();
    // load() is stable enough for our purposes; depending on it would re-run on every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const logout = () => { localStorage.removeItem("el_admin_token"); navigate("/admin/login"); };

  const updateStatus = async (id, status) => {
    try { await adminUpdateStatus(id, status); await load(); toast.success("Estado actualizado"); }
    catch (err) { toast.error(formatErr(err)); }
  };

  const removeLead = async (id) => {
    if (!confirm("¿Eliminar este lead?")) return;
    try { await adminDeleteLead(id); await load(); toast.success("Eliminado"); }
    catch (err) { toast.error(formatErr(err)); }
  };

  const markRead = async () => {
    try { await adminMarkNotificationsRead(); const n = await adminNotifications(); setNotifs(n); }
    catch (err) { toast.error(formatErr(err)); }
  };

  const filtered = filter === "all" ? leads : leads.filter((l) => l.status === filter);

  return (
    <div className="min-h-dvh bg-slate-50">
      <header className="sticky top-0 z-30 bg-white border-b border-slate-200">
        <div className="max-w-6xl mx-auto px-5 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Logo size={32} withWordmark />
            <span className="hidden sm:inline-flex text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-500 px-2 py-0.5 rounded-full border border-slate-200">Admin</span>
          </div>
          <div className="flex items-center gap-2">
            <NotifPopover notifs={notifs} onMarkRead={markRead} onOpenLead={() => setTab("leads")} />
            <ChangePasswordDialog />
            <span className="hidden sm:block text-sm text-slate-600">{me?.email}</span>
            <button onClick={load}
              className="h-9 w-9 rounded-full hover:bg-slate-100 flex items-center justify-center"
              data-testid="btn-admin-refresh" aria-label="Recargar">
              <RefreshCw className={`w-4 h-4 text-slate-700 ${loading ? "animate-spin" : ""}`} />
            </button>
            <button onClick={logout}
              className="h-9 px-3 rounded-full hover:bg-slate-100 flex items-center gap-1.5 text-sm font-medium text-slate-700"
              data-testid="btn-admin-logout">
              <LogOut className="w-4 h-4" />Salir
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-5 py-6 fade-in">
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="admin-stats">
          <Kpi label="Total" value={stats?.total ?? 0} icon={<Users className="w-4 h-4" />} color="#0F172A" />
          <Kpi label="Pendiente" value={stats?.pendiente ?? 0} icon={<Layers className="w-4 h-4" />} color="#F59E0B" />
          <Kpi label="En proceso" value={stats?.en_proceso ?? 0} icon={<CircleDot className="w-4 h-4" />} color="#3B82F6" />
          <Kpi label="Entregado" value={stats?.entregado ?? 0} icon={<CheckCircle2 className="w-4 h-4" />} color="#10B981" />
          <Kpi label="Vendedores" value={stats?.vendors ?? 0} icon={<Briefcase className="w-4 h-4" />} color="#9B2226" />
        </div>

        <Tabs value={tab} onValueChange={setTab} className="mt-5">
          <TabsList data-testid="admin-tabs">
            <TabsTrigger value="leads" data-testid="tab-leads">Leads</TabsTrigger>
            <TabsTrigger value="vendors" data-testid="tab-vendors">Vendedores</TabsTrigger>
            <TabsTrigger value="resources" data-testid="tab-resources">Recursos</TabsTrigger>
          </TabsList>

          <TabsContent value="leads">
            <div className="mt-2 flex flex-wrap items-center gap-2">
              <FilterChip active={filter === "all"} onClick={() => setFilter("all")} label={`Todos (${leads.length})`} testid="filter-all" />
              {STATUS_OPTS.map((s) => (
                <FilterChip key={s.value} active={filter === s.value} onClick={() => setFilter(s.value)}
                  label={s.label} dotColor={s.color} testid={`filter-${s.value}`} />
              ))}
            </div>
            <div className="mt-4 rounded-2xl border border-slate-200 bg-white overflow-hidden" data-testid="admin-leads-table">
              <div className="hidden md:grid grid-cols-12 px-4 py-3 text-[11px] font-semibold uppercase tracking-[0.12em] text-slate-500 bg-slate-50 border-b border-slate-200">
                <div className="col-span-3">Cliente</div>
                <div className="col-span-3">Negocio · Vendedor</div>
                <div className="col-span-2">Plan · Pagos</div>
                <div className="col-span-2">Estado</div>
                <div className="col-span-2 text-right">Acciones</div>
              </div>
              {filtered.length === 0 && (
                <div className="px-4 py-10 text-center text-slate-500 text-sm">
                  {loading ? "Cargando..." : "No hay leads en este filtro."}
                </div>
              )}
              {filtered.map((l) => (
                <LeadRow key={l.id} lead={l} vendors={vendors}
                  onUpdateStatus={updateStatus} onDelete={removeLead} onReload={load} />
              ))}
            </div>
          </TabsContent>

          <TabsContent value="vendors">
            <VendorsPane vendors={vendors} leads={leads} />
          </TabsContent>

          <TabsContent value="resources">
            <ResourcesPane resources={resources} onReload={load} />
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}

/* ---- Settings (password + 2FA) ---- */
const ChangePasswordDialog = () => {
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState("password");
  const [cur, setCur] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [saving, setSaving] = useState(false);

  // 2FA state
  const [twofaEnabled, setTwofaEnabled] = useState(false);
  const [setupData, setSetupData] = useState(null); // { secret, qr_data_url, otpauth_url }
  const [code, setCode] = useState("");
  const [recovery, setRecovery] = useState([]);

  useEffect(() => {
    if (open) {
      admin2faStatus().then((s) => setTwofaEnabled(!!s.enabled)).catch(() => {});
    } else {
      setSetupData(null); setCode(""); setRecovery([]);
    }
  }, [open]);

  const submitPwd = async (e) => {
    e.preventDefault();
    if (next.length < 8) { toast.error("Mínimo 8 caracteres"); return; }
    if (next !== confirm) { toast.error("Las contraseñas no coinciden"); return; }
    setSaving(true);
    try {
      await adminChangePassword(cur, next);
      toast.success("Contraseña actualizada");
      setOpen(false); setCur(""); setNext(""); setConfirm("");
    } catch (err) { toast.error(formatErr(err)); }
    finally { setSaving(false); }
  };

  const startSetup = async () => {
    try { setSetupData(await admin2faSetup()); }
    catch (err) { toast.error(formatErr(err)); }
  };

  const enable2fa = async () => {
    setSaving(true);
    try {
      const res = await admin2faEnable(code);
      setRecovery(res.recovery_codes);
      setTwofaEnabled(true);
      setSetupData(null); setCode("");
      toast.success("2FA activado");
    } catch (err) { toast.error(formatErr(err)); }
    finally { setSaving(false); }
  };

  const disable2fa = async () => {
    setSaving(true);
    try {
      await admin2faDisable(code);
      setTwofaEnabled(false); setCode("");
      toast.success("2FA desactivado");
    } catch (err) { toast.error(formatErr(err)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button className="h-9 w-9 rounded-full hover:bg-slate-100 flex items-center justify-center"
          data-testid="btn-admin-settings" aria-label="Ajustes">
          <Settings className="w-4 h-4 text-slate-700" />
        </button>
      </DialogTrigger>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Ajustes</DialogTitle>
          <DialogDescription>Seguridad de tu cuenta admin.</DialogDescription>
        </DialogHeader>

        <Tabs value={tab} onValueChange={setTab}>
          <TabsList>
            <TabsTrigger value="password" data-testid="tab-pwd">Contraseña</TabsTrigger>
            <TabsTrigger value="2fa" data-testid="tab-2fa">2FA</TabsTrigger>
          </TabsList>

          <TabsContent value="password" className="mt-4">
            <form onSubmit={submitPwd} className="space-y-3" data-testid="change-password-form">
              <div className="space-y-1.5">
                <Label className="text-sm">Contraseña actual</Label>
                <Input type="password" value={cur} onChange={(e) => setCur(e.target.value)}
                  data-testid="input-cur-pwd" required />
              </div>
              <div className="space-y-1.5">
                <Label className="text-sm">Nueva contraseña (mín. 8)</Label>
                <Input type="password" value={next} onChange={(e) => setNext(e.target.value)}
                  data-testid="input-new-pwd" required />
              </div>
              <div className="space-y-1.5">
                <Label className="text-sm">Confirmar</Label>
                <Input type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)}
                  data-testid="input-confirm-pwd" required />
              </div>
              <DialogFooter>
                <Button type="submit" disabled={saving} className="btn-primary"
                  data-testid="btn-change-pwd-submit">
                  {saving ? "Guardando..." : "Actualizar"}
                </Button>
              </DialogFooter>
            </form>
          </TabsContent>

          <TabsContent value="2fa" className="mt-4 space-y-3">
            {recovery.length > 0 && (
              <div className="rounded-xl border border-amber-200 bg-amber-50 p-3 text-sm">
                <div className="font-bold text-amber-900">Guarda estos códigos de recuperación</div>
                <div className="text-xs text-amber-800 mt-0.5">Te servirán si pierdes acceso a tu app.</div>
                <div className="mt-2 grid grid-cols-2 gap-1.5 font-mono text-[11px] text-amber-900" data-testid="recovery-codes">
                  {recovery.map((r) => <span key={r} className="bg-white px-2 py-1 rounded">{r}</span>)}
                </div>
              </div>
            )}

            {!twofaEnabled && !setupData && (
              <>
                <p className="text-sm text-slate-600">
                  Añade una capa extra de seguridad usando una app de autenticación (Google Authenticator, Authy…).
                </p>
                <Button onClick={startSetup} className="btn-primary w-full h-11 rounded-xl"
                  data-testid="btn-2fa-setup">
                  Activar 2FA
                </Button>
              </>
            )}

            {setupData && (
              <div className="space-y-3 text-center" data-testid="2fa-setup-block">
                <p className="text-sm text-slate-600">Escanea este código con tu app:</p>
                <img src={setupData.qr_data_url} alt="QR 2FA"
                  className="mx-auto rounded-xl border border-slate-200" width={180} height={180} />
                <p className="text-[11px] text-slate-500 break-all">
                  Secret: <span className="font-mono">{setupData.secret}</span>
                </p>
                <Input value={code} onChange={(e) => setCode(e.target.value)}
                  placeholder="Código de 6 dígitos"
                  className="h-12 text-center tracking-[0.4em] font-bold rounded-xl"
                  data-testid="input-2fa-enable-code" />
                <Button onClick={enable2fa} disabled={saving || code.length < 6}
                  className="btn-primary w-full h-11 rounded-xl"
                  data-testid="btn-2fa-enable">
                  {saving ? "Verificando..." : "Confirmar y activar"}
                </Button>
              </div>
            )}

            {twofaEnabled && recovery.length === 0 && (
              <div className="space-y-3">
                <div className="flex items-center gap-2 text-emerald-700 bg-emerald-50 border border-emerald-100 rounded-xl px-3 py-2 text-sm font-semibold">
                  <CheckCircle2 className="w-4 h-4" />2FA activado
                </div>
                <Input value={code} onChange={(e) => setCode(e.target.value)}
                  placeholder="Código actual para desactivar"
                  className="h-11 rounded-xl text-center tracking-[0.3em] font-bold"
                  data-testid="input-2fa-disable-code" />
                <Button onClick={disable2fa} disabled={saving || code.length < 6}
                  variant="outline" className="w-full h-11 rounded-xl"
                  data-testid="btn-2fa-disable">
                  {saving ? "Desactivando..." : "Desactivar 2FA"}
                </Button>
              </div>
            )}
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
};

/* ---- Notifications ---- */
const NotifPopover = ({ notifs, onMarkRead, onOpenLead }) => (
  <Dialog>
    <DialogTrigger asChild>
      <button className="relative h-9 w-9 rounded-full hover:bg-slate-100 flex items-center justify-center"
        data-testid="btn-notifs" aria-label="Notificaciones">
        <Bell className="w-4 h-4 text-slate-700" />
        {notifs.unread > 0 && (
          <span className="absolute -top-0.5 -right-0.5 bg-[var(--el-red)] text-white text-[10px] font-bold rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1">
            {notifs.unread}
          </span>
        )}
      </button>
    </DialogTrigger>
    <DialogContent className="max-w-md">
      <DialogHeader>
        <DialogTitle>Notificaciones</DialogTitle>
        <DialogDescription>Últimos eventos</DialogDescription>
      </DialogHeader>
      <div className="max-h-[400px] overflow-auto space-y-2">
        {notifs.items.length === 0 && <p className="text-sm text-slate-500">Sin notificaciones</p>}
        {notifs.items.map((n) => (
          <div key={n.id} className={`rounded-xl border p-3 ${n.read ? "bg-white border-slate-200" : "bg-[var(--el-red)]/5 border-[var(--el-red)]/30"}`}>
            <div className="font-semibold text-slate-900 text-sm">{n.title}</div>
            <div className="text-xs text-slate-600 mt-0.5">{n.body}</div>
            <div className="text-[10px] text-slate-400 mt-1">{new Date(n.created_at).toLocaleString("es-ES")}</div>
          </div>
        ))}
      </div>
      <DialogFooter>
        <Button variant="outline" onClick={onMarkRead} data-testid="btn-mark-read">Marcar leídas</Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
);

/* ---- Lead row ---- */
const LeadRow = ({ lead, vendors, onUpdateStatus, onDelete, onReload }) => {
  const status = STATUS_OPTS.find((s) => s.value === lead.status);
  return (
    <div className="grid grid-cols-1 md:grid-cols-12 gap-2 md:gap-0 px-4 py-3 border-b last:border-b-0 border-slate-100 hover:bg-slate-50/50 transition items-center"
      data-testid={`lead-row-${lead.id}`}>
      <div className="md:col-span-3">
        <div className="font-semibold text-slate-900 text-sm">{lead.contact_name}</div>
        <div className="text-xs text-slate-500 truncate">{lead.email}</div>
      </div>
      <div className="md:col-span-3">
        <div className="text-sm text-slate-900 truncate">{lead.business_name || lead.business_type}</div>
        <div className="text-xs text-slate-500 truncate">
          {lead.vendor_code ? `Vendedor: ${lead.vendor_code}` : "Sin vendedor"}
        </div>
      </div>
      <div className="md:col-span-2">
        <div className="text-sm font-semibold text-slate-900 uppercase">{lead.selected_plan || "—"}</div>
        <div className="text-[11px] flex gap-1 mt-0.5">
          <PayChip label="1º" ok={lead.paid_first} />
          <PayChip label="2º" ok={lead.paid_second} />
        </div>
      </div>
      <div className="md:col-span-2">
        <Select value={lead.status} onValueChange={(v) => onUpdateStatus(lead.id, v)}>
          <SelectTrigger className="h-9" data-testid={`status-select-${lead.id}`}>
            <SelectValue>
              <span className="flex items-center gap-2">
                <span className="w-2 h-2 rounded-full" style={{ backgroundColor: status?.color }} />
                {status?.label}
              </span>
            </SelectValue>
          </SelectTrigger>
          <SelectContent>
            {STATUS_OPTS.map((s) => (
              <SelectItem key={s.value} value={s.value}>
                <span className="flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full" style={{ backgroundColor: s.color }} />
                  {s.label}
                </span>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div className="md:col-span-2 flex items-center justify-end gap-1.5">
        <EditLeadDialog lead={lead} vendors={vendors} onReload={onReload} />
        <Button onClick={() => onDelete(lead.id)} variant="ghost" size="sm"
          className="h-8 w-8 p-0 text-red-600 hover:bg-red-50"
          data-testid={`btn-delete-${lead.id}`} aria-label="Eliminar">
          <Trash2 className="w-4 h-4" />
        </Button>
      </div>
    </div>
  );
};

const PayChip = ({ label, ok }) => (
  <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-semibold ${
    ok ? "bg-emerald-50 text-emerald-700 border border-emerald-100"
       : "bg-slate-50 text-slate-500 border border-slate-200"
  }`}>
    {label}: {ok ? "✓" : "·"}
  </span>
);

/* ---- Edit lead dialog: upload prototype + set vendor + mark paid ---- */
const EditLeadDialog = ({ lead, vendors, onReload }) => {
  const [open, setOpen] = useState(false);
  const [prototype, setPrototype] = useState(lead.prototype_url || "");
  const [vendorCode, setVendorCode] = useState(lead.vendor_code || "");
  const [paidFirst, setPaidFirst] = useState(!!lead.paid_first);
  const [paidSecond, setPaidSecond] = useState(!!lead.paid_second);
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      await adminUpdateLead(lead.id, {
        prototype_url: prototype || null,
        vendor_code: vendorCode ? vendorCode.toUpperCase() : null,
        paid_first: paidFirst,
        paid_second: paidSecond,
      });
      toast.success("Guardado");
      setOpen(false);
      onReload();
    } catch (err) { toast.error(formatErr(err)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="ghost" size="sm" className="h-8 w-8 p-0"
          data-testid={`btn-edit-${lead.id}`} aria-label="Editar">
          <Eye className="w-4 h-4" />
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{lead.contact_name}</DialogTitle>
          <DialogDescription>{lead.email}</DialogDescription>
        </DialogHeader>

        <div className="space-y-4 text-sm">
          <Section title="Proyecto">
            <KV k="Negocio" v={lead.business_name || "—"} />
            <KV k="Tipo" v={lead.business_type} />
            <KV k="Estilo" v={lead.style ? `${lead.style}${lead.style_other ? ` · ${lead.style_other}` : ""}` : "—"} />
            <KV k="Mejorar" v={lead.improve?.join(", ") || "—"} />
            <KV k="Contacto" v={lead.current_contact} />
            <KV k="Implementar" v={lead.implement?.join(", ") || "—"} />
            <KV k="Plan" v={(lead.selected_plan || "—").toUpperCase()} />
            <KV k="Código" v={lead.access_code} />
          </Section>

          <Section title="Prototipo" icon={<Link2 className="w-3.5 h-3.5" />}>
            <Input value={prototype} onChange={(e) => setPrototype(e.target.value)}
              placeholder="https://..." className="h-10 rounded-xl"
              data-testid="input-prototype-url" />
            {lead.prototype_url && (
              <a href={lead.prototype_url} target="_blank" rel="noreferrer"
                className="inline-flex items-center gap-1 text-xs text-[var(--el-red)] mt-1.5">
                <ExternalLink className="w-3 h-3" />Abrir actual
              </a>
            )}
          </Section>

          <Section title="Vendedor">
            <Select value={vendorCode || "none"} onValueChange={(v) => setVendorCode(v === "none" ? "" : v)}>
              <SelectTrigger className="h-10" data-testid="select-vendor">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Sin vendedor</SelectItem>
                {vendors.map((v) => (
                  <SelectItem key={v.id} value={v.vendor_code}>
                    {v.vendor_code} · {v.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Section>

          <Section title="Pagos" icon={<CreditCard className="w-3.5 h-3.5" />}>
            <div className="flex items-center gap-3 flex-wrap">
              <CheckToggle label="1er pago (50%)" checked={paidFirst} onChange={setPaidFirst} testid="chk-paid-first" />
              <CheckToggle label="2do pago (50%)" checked={paidSecond} onChange={setPaidSecond} testid="chk-paid-second" />
            </div>
            {lead.selected_plan && lead.selected_plan !== "gratuito" && !paidSecond && (
              <Button type="button" variant="outline" size="sm"
                className="mt-2 text-xs"
                data-testid={`btn-send-2nd-pay-${lead.id}`}
                onClick={async () => {
                  try {
                    const r = await adminSendSecondPayment(lead.id);
                    toast.success("Enlace de 2º pago enviado al cliente");
                    if (r?.url) navigator.clipboard.writeText(r.url);
                  } catch (err) { toast.error(formatErr(err)); }
                }}>
                Enviar enlace 2º pago al cliente
              </Button>
            )}
          </Section>
        </div>

        <DialogFooter>
          <Button onClick={save} disabled={saving} className="btn-primary"
            data-testid={`btn-save-lead-${lead.id}`}>
            {saving ? "Guardando..." : "Guardar cambios"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const Section = ({ title, icon, children }) => (
  <div>
    <div className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-[0.15em] text-slate-500 mb-1.5">
      {icon}{title}
    </div>
    <div className="space-y-1.5">{children}</div>
  </div>
);

const KV = ({ k, v }) => (
  <div className="flex items-start justify-between gap-3 text-xs">
    <span className="text-slate-500 uppercase tracking-wider">{k}</span>
    <span className="text-slate-900 text-right break-words">{v}</span>
  </div>
);

const CheckToggle = ({ label, checked, onChange, testid }) => (
  <label className="inline-flex items-center gap-2 cursor-pointer select-none">
    <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)}
      data-testid={testid}
      className="h-4 w-4 rounded border-slate-300 text-[var(--el-red)] focus:ring-[var(--el-red)]" />
    <span className="text-sm text-slate-800">{label}</span>
  </label>
);

/* ---- Vendors pane ---- */
const VendorsPane = ({ vendors, leads }) => (
  <div className="mt-4 rounded-2xl border border-slate-200 bg-white overflow-hidden" data-testid="admin-vendors-table">
    <div className="hidden md:grid grid-cols-12 px-4 py-3 text-[11px] font-semibold uppercase tracking-[0.12em] text-slate-500 bg-slate-50 border-b border-slate-200">
      <div className="col-span-2">Código</div>
      <div className="col-span-3">Nombre</div>
      <div className="col-span-3">Email</div>
      <div className="col-span-2">Bizum</div>
      <div className="col-span-2 text-right">Clientes</div>
    </div>
    {vendors.length === 0 && (
      <div className="px-4 py-10 text-center text-slate-500 text-sm">Aún no hay vendedores registrados.</div>
    )}
    {vendors.map((v) => {
      const clients = leads.filter((l) => l.vendor_code === v.vendor_code).length;
      return (
        <div key={v.id}
          className="grid grid-cols-1 md:grid-cols-12 gap-1 md:gap-0 px-4 py-3 border-b last:border-b-0 border-slate-100 items-center"
          data-testid={`vendor-row-${v.id}`}>
          <div className="md:col-span-2 font-mono font-bold text-slate-900">{v.vendor_code}</div>
          <div className="md:col-span-3 text-sm text-slate-900">{v.name}</div>
          <div className="md:col-span-3 text-sm text-slate-600 truncate">{v.email}</div>
          <div className="md:col-span-2 text-xs text-slate-700">{v.phone_bizum || "—"}</div>
          <div className="md:col-span-2 text-right font-semibold text-slate-900">{clients}</div>
        </div>
      );
    })}
  </div>
);

/* ---- Resources pane ---- */
const ResourcesPane = ({ resources, onReload }) => {
  const [title, setTitle] = useState("");
  const [desc, setDesc] = useState("");
  const [url, setUrl] = useState("");
  const [saving, setSaving] = useState(false);

  const add = async (e) => {
    e.preventDefault();
    if (!title || !url) { toast.error("Título y URL son obligatorios"); return; }
    setSaving(true);
    try {
      await adminCreateResource({ title, description: desc || null, url });
      setTitle(""); setDesc(""); setUrl("");
      await onReload();
      toast.success("Recurso añadido");
    } catch (err) { toast.error(formatErr(err)); }
    finally { setSaving(false); }
  };

  const remove = async (id) => {
    if (!confirm("¿Eliminar recurso?")) return;
    try { await adminDeleteResource(id); await onReload(); toast.success("Eliminado"); }
    catch (err) { toast.error(formatErr(err)); }
  };

  return (
    <div className="mt-4 grid md:grid-cols-5 gap-4">
      <form onSubmit={add} className="md:col-span-2 rounded-2xl border border-slate-200 bg-white p-4 space-y-3"
        data-testid="resource-form">
        <div className="flex items-center gap-2">
          <Plus className="w-4 h-4 text-[var(--el-red)]" />
          <h3 className="font-[Outfit] font-semibold text-slate-900">Añadir recurso</h3>
        </div>
        <div className="space-y-1.5">
          <Label className="text-sm">Título</Label>
          <Input value={title} onChange={(e) => setTitle(e.target.value)}
            placeholder="Guía de ventas" data-testid="input-res-title" />
        </div>
        <div className="space-y-1.5">
          <Label className="text-sm">Descripción</Label>
          <Textarea value={desc} onChange={(e) => setDesc(e.target.value)}
            placeholder="Breve descripción (opcional)" rows={3} data-testid="input-res-desc" />
        </div>
        <div className="space-y-1.5">
          <Label className="text-sm">URL del PDF / archivo</Label>
          <Input value={url} onChange={(e) => setUrl(e.target.value)}
            placeholder="https://..." data-testid="input-res-url" />
        </div>
        <Button type="submit" disabled={saving} className="btn-primary w-full h-11 rounded-xl"
          data-testid="btn-res-submit">
          {saving ? "Guardando..." : "Publicar"}
        </Button>
      </form>

      <div className="md:col-span-3">
        <div className="rounded-2xl border border-slate-200 bg-white">
          <div className="px-4 py-3 text-[11px] font-semibold uppercase tracking-[0.12em] text-slate-500 bg-slate-50 border-b border-slate-200 flex items-center gap-2">
            <BookOpen className="w-3.5 h-3.5" /> Recursos publicados ({resources.length})
          </div>
          {resources.length === 0 && (
            <div className="px-4 py-10 text-center text-slate-500 text-sm">Aún no hay recursos.</div>
          )}
          {resources.map((r) => (
            <div key={r.id} className="flex items-center gap-3 px-4 py-3 border-b last:border-b-0 border-slate-100"
              data-testid={`res-row-${r.id}`}>
              <div className="h-10 w-10 rounded-xl bg-[var(--el-red)]/10 text-[var(--el-red)] flex items-center justify-center shrink-0">
                <FileText className="w-4 h-4" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-slate-900 text-sm truncate">{r.title}</div>
                {r.description && <div className="text-xs text-slate-500 truncate">{r.description}</div>}
                <a href={r.url} target="_blank" rel="noreferrer"
                  className="text-xs text-[var(--el-red)] hover:underline truncate inline-flex items-center gap-1">
                  <ExternalLink className="w-3 h-3" />{r.url.slice(0, 40)}
                </a>
              </div>
              <Button onClick={() => remove(r.id)} variant="ghost" size="sm"
                className="h-8 w-8 p-0 text-red-600 hover:bg-red-50"
                data-testid={`btn-res-del-${r.id}`}>
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

/* ---- Small bits ---- */
const Kpi = ({ label, value, icon, color }) => (
  <div className="rounded-2xl border border-slate-200 bg-white p-4" data-testid={`kpi-${label}`}>
    <div className="flex items-center justify-between">
      <span className="text-xs font-semibold uppercase tracking-[0.12em] text-slate-500">{label}</span>
      <span className="h-7 w-7 rounded-lg flex items-center justify-center text-white" style={{ backgroundColor: color }}>
        {icon}
      </span>
    </div>
    <div className="mt-2 text-2xl font-[Outfit] font-extrabold text-slate-900">{value}</div>
  </div>
);

const FilterChip = ({ active, onClick, label, dotColor, testid }) => (
  <button onClick={onClick} data-testid={testid}
    className={`px-3 h-8 rounded-full text-xs font-semibold flex items-center gap-1.5 transition ${
      active ? "bg-slate-900 text-white"
             : "bg-white border border-slate-200 text-slate-700 hover:bg-slate-50"
    }`}>
    {dotColor && <span className="w-2 h-2 rounded-full" style={{ backgroundColor: dotColor }} />}
    {label}
  </button>
);
